import { useEffect, useState } from 'react';
import {
  ThongTinDoanhNghiep,
  ThongTinDoanhNghiepInput,
  ThongTinDoanhNghiepResponse
} from '@/types/schemas/thong-tin-doanh-nghiep.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseThongTinDoanhNghiepReturn {
  businessInfo: ThongTinDoanhNghiep[];
  isLoading: boolean;
  addBusinessInfo: (newBusinessInfo: ThongTinDoanhNghiepInput) => Promise<ThongTinDoanhNghiep>;
  updateBusinessInfo: (uuid: string, updatedBusinessInfo: ThongTinDoanhNghiepInput) => Promise<ThongTinDoanhNghiep>;
  deleteBusinessInfo: (uuid: string) => Promise<void>;
  refreshBusinessInfo: () => Promise<void>;
  getBusinessInfoById: (uuid: string) => Promise<ThongTinDoanhNghiep | undefined>;
}

/**
 * Hook for managing ThongTinDoanhNghiep (Business Information) data
 *
 * This hook provides functions to fetch, create, update, and delete business information.
 */
export const useThongTinDoanhNghiep = (
  initialBusinessInfo: ThongTinDoanhNghiep[] = []
): UseThongTinDoanhNghiepReturn => {
  const [businessInfo, setBusinessInfo] = useState<ThongTinDoanhNghiep[]>(initialBusinessInfo);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchBusinessInfo = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<ThongTinDoanhNghiepResponse>(`/entities/${entity.slug}/erp/business-info/`);
      setBusinessInfo(response.data.results);
    } catch (error) {
      console.error('Error fetching business information:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getBusinessInfoById = async (uuid: string): Promise<ThongTinDoanhNghiep | undefined> => {
    if (!entity?.slug) return undefined;

    try {
      const response = await api.get<ThongTinDoanhNghiep>(`/entities/${entity.slug}/erp/business-info/${uuid}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching business information with UUID ${uuid}:`, error);
      return undefined;
    }
  };

  const addBusinessInfo = async (newBusinessInfo: ThongTinDoanhNghiepInput): Promise<ThongTinDoanhNghiep> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.post<ThongTinDoanhNghiep>(
        `/entities/${entity.slug}/erp/business-info/`,
        newBusinessInfo
      );
      const addedBusinessInfo = response.data;

      setBusinessInfo(prev => [...prev, addedBusinessInfo]);
      return addedBusinessInfo;
    } catch (error) {
      console.error('Error adding business information:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateBusinessInfo = async (
    uuid: string,
    updatedBusinessInfo: ThongTinDoanhNghiepInput
  ): Promise<ThongTinDoanhNghiep> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const response = await api.patch<ThongTinDoanhNghiep>(
        `/entities/${entity.slug}/erp/business-info/${uuid}/`,
        updatedBusinessInfo
      );
      const updatedBusinessInfoData = response.data;

      setBusinessInfo(prev =>
        prev.map(info => (info.uuid === updatedBusinessInfoData.uuid ? updatedBusinessInfoData : info))
      );

      return updatedBusinessInfoData;
    } catch (error) {
      console.error('Error updating business information:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBusinessInfo = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/business-info/${uuid}/`);
      setBusinessInfo(prev => prev.filter(info => info.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting business information:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessInfo();
  }, [entity?.slug]);

  return {
    businessInfo,
    isLoading,
    addBusinessInfo,
    updateBusinessInfo,
    deleteBusinessInfo,
    refreshBusinessInfo: fetchBusinessInfo,
    getBusinessInfoById
  };
};
